/**
 * Google Analytics 4 helper with consent-gating.
 *
 * Nothing is loaded or fired until `setConsent(true)` is called. Consent is
 * persisted in `localStorage` for 12 months. Admin routes are excluded
 * entirely — we never load gtag.js on `/admin/*`.
 *
 * Use:
 *   import { setConsent, getConsent, trackEvent, trackPageView } from "../lib/analytics";
 *
 *   setConsent(true);                          // user accepted
 *   trackEvent("generate_lead", {lead_type:"quick"});
 *   trackPageView("/about");
 */

const STORAGE_KEY = "ridgehawk_consent";
const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000; // 12 months
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

const CONSENT_EVENT = "ridgehawk:consent-changed";
const OPEN_BANNER_EVENT = "ridgehawk:open-consent";

let gtagLoaded = false;
let pendingQueue = [];

function isAdminPath(pathname) {
  return typeof pathname === "string" && pathname.startsWith("/admin");
}

function currentPath() {
  return typeof window !== "undefined" && window.location ? window.location.pathname : "/";
}

export function gaConfigured() {
  return Boolean(GA_ID);
}

export function getConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.ts) return null;
    const age = Date.now() - new Date(parsed.ts).getTime();
    if (Number.isFinite(age) && age > CONSENT_TTL_MS) {
      // expired — treat as not-yet-decided so banner re-prompts.
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed; // { status: 'accepted'|'declined', ts }
  } catch {
    return null;
  }
}

function persistConsent(status) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ status, ts: new Date().toISOString() }),
    );
  } catch {
    /* quota — ignore */
  }
}

function ensureDataLayer() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

let consentDefaultSet = false;
function setDefaultConsent() {
  if (consentDefaultSet) return;
  ensureDataLayer();
  // Google Consent Mode v2 — deny analytics by default. gtag queues this BEFORE
  // the library script loads, so the eventual library respects it on init.
  // When the user accepts, we call gtag('consent','update',...) to flip to granted.
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
  consentDefaultSet = true;
}

function loadGtag() {
  if (typeof window === "undefined") return;
  if (!GA_ID) return;
  if (isAdminPath(currentPath())) return; // never load on admin
  ensureDataLayer();
  setDefaultConsent();

  if (!gtagLoaded) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    s.setAttribute("data-ridgehawk", "ga");
    document.head.appendChild(s);

    window.gtag("js", new Date());
    window.gtag("config", GA_ID, {
      anonymize_ip: true,
      send_page_view: false, // we fire page_view manually on every route change
    });

    gtagLoaded = true;
  }

  // Consent Mode v2 — analytics is now allowed.
  window.gtag("consent", "update", { analytics_storage: "granted" });

  // Flush any events queued before consent.
  if (pendingQueue.length) {
    const q = pendingQueue;
    pendingQueue = [];
    q.forEach(([name, params]) => {
      try { window.gtag("event", name, params || {}); } catch { /* noop */ }
    });
  }
}

export function setConsent(accepted) {
  const status = accepted ? "accepted" : "declined";
  persistConsent(status);
  if (accepted) {
    loadGtag();
    // Fire an initial page_view immediately after consent on the current page.
    trackPageView(currentPath());
  } else {
    pendingQueue = []; // drop any queued pre-consent events
    // If gtag was loaded in a prior accepted session, tell it to stop tracking now.
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      try {
        window.gtag("consent", "update", { analytics_storage: "denied" });
      } catch { /* noop */ }
    }
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { status } }));
  }
}

export function clearConsent() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { status: null } }));
  }
}

export function openConsentBanner() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(OPEN_BANNER_EVENT));
  }
}

export function onConsentChange(handler) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(CONSENT_EVENT, handler);
  return () => window.removeEventListener(CONSENT_EVENT, handler);
}

export function onOpenBanner(handler) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(OPEN_BANNER_EVENT, handler);
  return () => window.removeEventListener(OPEN_BANNER_EVENT, handler);
}

// On module load, prime Consent Mode v2 + auto-load gtag for returning visitors
// who already accepted.
(function bootstrap() {
  if (typeof window === "undefined") return;
  if (isAdminPath(currentPath()) || !GA_ID) return;
  setDefaultConsent();
  const c = getConsent();
  if (c && c.status === "accepted") {
    loadGtag();
  }
})();

export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  if (!GA_ID) return;
  if (isAdminPath(currentPath())) return; // never track admin
  const c = getConsent();
  if (!c || c.status !== "accepted") {
    // Queue minimally so a same-pageload acceptance still captures the event.
    pendingQueue.push([name, params]);
    if (pendingQueue.length > 20) pendingQueue.shift();
    return;
  }
  if (!gtagLoaded) loadGtag();
  try {
    window.gtag("event", name, params || {});
  } catch {
    /* noop */
  }
}

export function trackPageView(path) {
  const p = path || currentPath();
  if (isAdminPath(p)) return;
  trackEvent("page_view", { page_path: p, page_location: typeof window !== "undefined" ? window.location.href : p });
}
